/**
 * Bookings feature — UI-only presentation settings (three-bucket config, ADR 0002).
 *
 * Status palette, labels, and tab definitions. Backed by ``BookingStatus`` /
 * ``BookingType`` literals from the generated API types, which themselves
 * derive from ``app/features/bookings/config/rules.py``.
 */

import type { BookingStatus, BookingType } from '../api/bookings';

export interface StatusOption {
  value: BookingStatus;
  label: string;
  color: string;
}

export interface TypeOption {
  value: BookingType;
  label: string;
  /** Whether this type normally consumes an entitlement (informational only;
   * the backend is the authoritative source). */
  consumes: boolean;
}

export const BOOKING_STATUS_OPTIONS: readonly StatusOption[] = [
  { value: 'pending',   label: 'Pending',   color: '#f59e0b' },
  { value: 'confirmed', label: 'Confirmed', color: '#16a34a' },
  { value: 'cancelled', label: 'Cancelled', color: '#dc2626' },
  { value: 'completed', label: 'Completed', color: '#6366f1' },
  { value: 'no_show',   label: 'No Show',   color: '#9ca3af' },
] as const;

export const BOOKING_TYPE_OPTIONS: readonly TypeOption[] = [
  { value: 'trial',   label: 'Trial',   consumes: false },
  { value: 'makeup',  label: 'Make-up', consumes: true  },
  { value: 'ad_hoc',  label: 'Ad-hoc',  consumes: true  },
  { value: 'regular', label: 'Regular', consumes: true  },
] as const;

/** Returns the colour for a booking status (falls back to grey). */
export function statusColor(status: BookingStatus | string): string {
  return BOOKING_STATUS_OPTIONS.find((o) => o.value === status)?.color ?? '#9ca3af';
}

/** Returns the display label for a booking type. */
export function typeLabel(type: BookingType | string): string {
  return BOOKING_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

/** Returns the display label for a booking status. */
export function statusLabel(status: BookingStatus | string): string {
  return BOOKING_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

/** Tab definitions for the BookingsPage top-level navigation. */
export const BOOKING_TABS = [
  { id: 'all',     label: 'All'     },
  { id: 'trial',   label: 'Trials'  },
  { id: 'makeup',  label: 'Make-ups'},
  { id: 'ad_hoc',  label: 'Ad-hoc'  },
] as const;

export type BookingTabId = (typeof BOOKING_TABS)[number]['id'];

